/** Shared listing sort. Default is newest first. */

export type ListingSortValue = 'newest' | 'oldest' | 'name_asc' | 'name_desc';

export const DEFAULT_LISTING_SORT: ListingSortValue = 'newest';

export const LISTING_SORT_OPTIONS: Array<{ value: ListingSortValue; label: string }> = [
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'name_asc', label: 'Name A–Z' },
  { value: 'name_desc', label: 'Name Z–A' },
];

export interface ListingSortAccessors<T> {
  getDate?: (item: T) => unknown;
  getName?: (item: T) => unknown;
}

function readDate(value: unknown): number {
  if (value instanceof Date) {
    const t = value.getTime();
    return Number.isFinite(t) ? t : 0;
  }
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const t = Date.parse(value);
    return Number.isFinite(t) ? t : 0;
  }
  return 0;
}

function readName(value: unknown): string {
  return String(value ?? '').trim().toLowerCase();
}

export function getListingDate(item: unknown): number {
  if (!item || typeof item !== 'object') return 0;
  const row = item as Record<string, unknown>;
  const nested = row.assessment && typeof row.assessment === 'object'
    ? (row.assessment as Record<string, unknown>)
    : null;
  const application = row.application && typeof row.application === 'object'
    ? (row.application as Record<string, unknown>)
    : null;
  return readDate(
    row.created_at
      ?? row.createdAt
      ?? row.timestamp
      ?? row.submitted_at
      ?? row.updated_at
      ?? row.updatedAt
      ?? row.onboarded_at
      ?? row.last_assessment_date
      ?? nested?.created_at
      ?? nested?.completed_at
      ?? application?.created_at
  );
}

export function getListingName(item: unknown): string {
  if (!item || typeof item !== 'object') return '';
  const row = item as Record<string, unknown>;
  const application = row.application && typeof row.application === 'object'
    ? (row.application as Record<string, unknown>)
    : null;
  const first = String(row.first_name ?? '').trim();
  const last = String(row.last_name ?? '').trim();
  const fullName = `${first} ${last}`.trim();
  return readName(
    row.name
      ?? row.display_name
      ?? row.application_name
      ?? row.app_name
      ?? row.entitlement_name
      ?? row.entity_name
      ?? row.title
      ?? row.account_name
      ?? row.question_text
      ?? application?.name
      ?? (fullName || undefined)
      ?? row.email
      ?? row.username
  );
}

export function sortListing<T>(
  items: T[],
  sort: ListingSortValue = DEFAULT_LISTING_SORT,
  accessors: ListingSortAccessors<T> = {}
): T[] {
  const getDate = accessors.getDate
    ? (item: T) => readDate(accessors.getDate!(item))
    : (item: T) => getListingDate(item);
  const getName = accessors.getName
    ? (item: T) => readName(accessors.getName!(item))
    : (item: T) => getListingName(item);

  const copy = [...items];
  copy.sort((a, b) => {
    if (sort === 'newest' || sort === 'oldest') {
      const diff = getDate(b) - getDate(a);
      if (diff !== 0) return sort === 'oldest' ? -diff : diff;
      return getName(a).localeCompare(getName(b));
    }
    const cmp = getName(a).localeCompare(getName(b));
    if (cmp !== 0) return sort === 'name_desc' ? -cmp : cmp;
    return getDate(b) - getDate(a);
  });
  return copy;
}

/** Map UI sort to audit API query params (allowlisted on the backend). */
export function listingSortToAuditQuery(sort: ListingSortValue): { sort: string; order: 'asc' | 'desc' } {
  if (sort === 'oldest') return { sort: 'timestamp', order: 'asc' };
  if (sort === 'name_asc') return { sort: 'entity_name', order: 'asc' };
  if (sort === 'name_desc') return { sort: 'entity_name', order: 'desc' };
  return { sort: 'timestamp', order: 'desc' };
}
